import { useState, useRef, useEffect } from 'react';
import { sendMessage } from '../api/claude';

// Fields each template needs and their questions
const TEMPLATE_QUESTIONS = {
  default: [
    { field: 'name', q: "What's your full name?" },
    { field: 'email', q: "What's your email address?" },
    { field: 'phone', q: "What's your phone number?" },
    { field: 'location', q: "What's your city/location?" },
    { field: 'summary', q: "Write a short professional summary about yourself." },
    { field: 'experience', q: "Tell me about your work experience (company, role, and key achievements)." },
    { field: 'education', q: "What's your educational qualification (degree, college, year)?" },
    { field: 'skills', q: "List your top skills (e.g. React, Python, Leadership)." },
  ],
  withLinkedIn: [
    { field: 'name', q: "What's your full name?" },
    { field: 'email', q: "What's your email address?" },
    { field: 'phone', q: "What's your phone number?" },
    { field: 'location', q: "What's your city/location?" },
    { field: 'linkedin', q: "What's your LinkedIn profile URL?" },
    { field: 'summary', q: "Write a short professional summary about yourself." },
    { field: 'experience', q: "Tell me about your work experience (company, role, and key achievements)." },
    { field: 'education', q: "What's your educational qualification (degree, college, year)?" },
    { field: 'skills', q: "List your top skills (e.g. React, Python, Leadership)." },
  ],
};

const getQuestionsForTemplate = (template) => {
  if (!template) return TEMPLATE_QUESTIONS.default;
  if (template.fields.includes('linkedin')) return TEMPLATE_QUESTIONS.withLinkedIn;
  return TEMPLATE_QUESTIONS.default;
};

const getWelcomeMessage = (template) => {
  if (!template) return "Hi! I'm your AI resume builder 👋 Let's create a standout resume together. What's your full name?";
  return `Great choice! 🎨 You selected the **${template.name}** template. Let's build your resume step by step!\n\nWhat's your full name?`;
};

export default function ChatPanel({ onResumeUpdate, template }) {
  const getInitialMessages = () => [
    {
      role: 'assistant',
      content: JSON.stringify({
        message: getWelcomeMessage(template),
        resume: { name: '', email: '', phone: '', location: '', linkedin: '', summary: '', experience: [], education: [], skills: [] },
        score: { total: 0, breakdown: { contact: 0, summary: 0, experience: 0, education: 0, skills: 0 } },
      }),
    },
  ];

  const [messages, setMessages] = useState(getInitialMessages);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const parseMessage = (msg) => {
    try {
      const parsed = JSON.parse(msg.content);
      return parsed.message || msg.content;
    } catch {
      return msg.content;
    }
  };

  const handleSend = async () => {
    const trimmed = input.trim();
    if (!trimmed || loading) return;

    const userMsg = { role: 'user', content: trimmed };
    const updatedMessages = [...messages, userMsg];
    setMessages(updatedMessages);
    setInput('');
    setLoading(true);

    try {
      const questions = getQuestionsForTemplate(template);
      const fieldsList = questions.map(q => q.field).join(', ');
      const templateInfo = template ? `User selected the "${template.name}" template which needs these fields: ${fieldsList}.` : '';

      const payload = updatedMessages.map((m) => ({
        role: m.role,
        content: m.content,
      }));

      const data = await sendMessage(payload, templateInfo);

      const assistantMsg = {
        role: 'assistant',
        content: JSON.stringify(data),
      };

      setMessages((prev) => [...prev, assistantMsg]);
      if (data.resume) onResumeUpdate(data.resume, data.score);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: JSON.stringify({ message: `Sorry, something went wrong: ${err.message}` }),
        },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div style={styles.container}>
      <style>{`
        @media (max-width: 480px) {
          .chat-header { padding: 12px 14px !important; }
          .chat-messages { padding: 14px !important; gap: 10px !important; }
          .chat-bubble { max-width: 92% !important; padding: 10px 12px !important; }
          .chat-input-row { padding: 10px 12px !important; }
          .chat-input-row textarea { font-size: 16px !important; }
        }
      `}</style>
      <div className="chat-header" style={styles.header}>
        <span style={styles.headerIcon}>✦</span>
        <span style={styles.headerTitle}>AI ResumeBuilder</span>
        {template && (
          <span style={{ marginLeft: 'auto', fontSize: '11px', background: '#1a1a1a', border: '1px solid #333', color: '#a78bfa', padding: '3px 10px', borderRadius: '20px' }}>
            {template.name} Template
          </span>
        )}
      </div>

      <div className="chat-messages" style={styles.messages}>
        {messages.map((msg, i) => (
          <div key={i} className="chat-bubble" style={{ ...styles.bubble, ...(msg.role === 'user' ? styles.userBubble : styles.aiBubble) }}>
            {msg.role === 'assistant' && <div style={styles.aiLabel}>AI</div>}
            <p style={styles.bubbleText}>{parseMessage(msg)}</p>
          </div>
        ))}
        {loading && (
          <div className="chat-bubble" style={{ ...styles.bubble, ...styles.aiBubble }}>
            <div style={styles.aiLabel}>AI</div>
            <p style={styles.typing}>Thinking...</p>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      <div className="chat-input-row" style={styles.inputRow}>
        <textarea
          style={styles.input}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type your answer..."
          rows={2}
        />
        <button style={{ ...styles.sendBtn, opacity: loading ? 0.5 : 1 }} onClick={handleSend} disabled={loading}>➤</button>
      </div>
    </div>
  );
}

const styles = {
  container: { display: 'flex', flexDirection: 'column', height: '100%', background: '#0f0f0f', color: '#f0f0f0', fontFamily: "'Inter', sans-serif" },
  header: { padding: '16px 20px', borderBottom: '1px solid #222', display: 'flex', alignItems: 'center', gap: '10px' },
  headerIcon: { fontSize: '18px', color: '#a78bfa' },
  headerTitle: { fontWeight: '600', fontSize: '15px', letterSpacing: '0.5px' },
  messages: { flex: 1, overflowY: 'auto', padding: '20px', display: 'flex', flexDirection: 'column', gap: '14px' },
  bubble: { maxWidth: '85%', padding: '12px 16px', borderRadius: '14px', lineHeight: '1.5' },
  aiBubble: { background: '#1a1a1a', border: '1px solid #2a2a2a', alignSelf: 'flex-start' },
  userBubble: { background: '#a78bfa', color: '#fff', alignSelf: 'flex-end' },
  aiLabel: { fontSize: '10px', fontWeight: '700', color: '#a78bfa', letterSpacing: '1px', marginBottom: '4px' },
  bubbleText: { fontSize: '14px', margin: 0 },
  typing: { fontSize: '14px', color: '#666', margin: 0 },
  inputRow: { display: 'flex', gap: '10px', padding: '16px 20px', borderTop: '1px solid #222', alignItems: 'flex-end' },
  input: { flex: 1, background: '#1a1a1a', border: '1px solid #333', borderRadius: '10px', color: '#f0f0f0', padding: '10px 14px', fontSize: '14px', resize: 'none', outline: 'none', fontFamily: 'inherit' },
  sendBtn: { background: '#a78bfa', border: 'none', borderRadius: '10px', color: '#fff', width: '42px', height: '42px', fontSize: '16px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
